// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import Icon from '@ant-design/icons';

import { ActiveControl, CombinedState } from 'reducers';
import { Canvas } from 'cvat-canvas-wrapper';
import { CommentIcon } from 'icons'; // Your custom icon
import CVATTooltip from 'components/common/cvat-tooltip';
import GlobalHotKeys from 'utils/mousetrap-react';
import { ShortcutScope } from 'utils/enums';
import { registerComponentShortcuts } from 'actions/shortcuts-actions';
import { subKeyMap } from 'utils/component-subkeymap';
import { useSelector, useDispatch } from 'react-redux';
import { reviewActions} from 'actions/review-actions';
import { NewIssueSource } from 'reducers';


interface Props {
    canvasInstance: Canvas; 
    activeControl: ActiveControl;
    disabled: boolean;
    updateActiveControl(activeControl: ActiveControl): void;
}

// Reusing the default issue action
const componentShortcuts = {
    OPEN_ISSUE: {
        name: 'Open general issue',
        description: 'Create a general issue for the current frame',
        sequences: ['g'],
        scope: ShortcutScope.REVIEW_WORKSPACE_CONTROLS,
    },
};

registerComponentShortcuts(componentShortcuts);

function CreateGeneralIssueControl(props: Props): JSX.Element {
    const {
        activeControl, canvasInstance, updateActiveControl, disabled,
    } = props;

    const { keyMap } = useSelector((state: CombinedState) => state.shortcuts);
    const dispatch = useDispatch();

    const handler = (): void => {
        if (activeControl === ActiveControl.OPEN_GENERAL_ISSUE) {
            canvasInstance.selectRegion(false);
            updateActiveControl(ActiveControl.CURSOR);
        } else {
            canvasInstance.cancel();
            canvasInstance.selectRegion(true);
            updateActiveControl(ActiveControl.OPEN_GENERAL_ISSUE);
            dispatch(reviewActions.switchIssueSource(NewIssueSource.GENERAL_COMMENT_TOOL));

        }
    };
    

    const handlers: Record<keyof typeof componentShortcuts, (event?: KeyboardEvent) => void> = {
        OPEN_ISSUE: (event?: KeyboardEvent) => {
            if (event) event.preventDefault();
            handler();
        },
    };

    return (
        disabled ? (
            <Icon component={CommentIcon} className='cvat-general-issue-control cvat-disabled-canvas-control' />
        ) : (
            <>
                <GlobalHotKeys
                    keyMap={subKeyMap(componentShortcuts, keyMap)}
                    handlers={handlers}
                />
                <CVATTooltip title='Open a general comment' placement='right'>
                    <Icon
                        component={CommentIcon}
                        className={
                            activeControl === ActiveControl.OPEN_GENERAL_ISSUE ?
                                'cvat-general-issue-control cvat-active-canvas-control' :
                                'cvat-general-issue-control'
                        }
                        onClick={handler}
                    />
                </CVATTooltip>
            </>
        )
    );
}

export default React.memo(CreateGeneralIssueControl);
